/**
 * src/layouts/registry.js
 *
 * Registry of available UI shell layouts and the user-facing "versions"
 * built on top of them.
 *
 * A "layout" is the structural skeleton (sidebar shape, presence of a top
 * bar). A "version" is what super-admin sees: a coarse choice (Beta UI vs
 * Aurora) that decides the layout AND constrains which colour themes the
 * end user can pick in their User Settings.
 *
 * Selection is global per-environment (stored on the tenant_registry row,
 * written from the super-admin Layouts tab).
 */

export const DEFAULT_LAYOUT_ID = 'beta';

export const LAYOUTS = [
  {
    id: 'beta',
    name: 'Beta UI',
    description:
      'Original SeaSuite shell. Sidebar with embedded user card, action centre, and bug report. No top bar.',
  },
  {
    id: 'aurora',
    name: 'Aurora',
    description:
      'Aurora shell — top bar with Tryggr wordmark, action centre, user dropdown, and a flat single-column sidebar. Indigo accent.',
  },
];

// Legacy ids that pre-date the version model. Map them onto the umbrella
// `aurora` layout so existing tenant_registry rows keep working without a
// migration.
const LEGACY_LAYOUT_ALIASES = {
  'aurora-dark': 'aurora',
  'aurora-light': 'aurora',
};

export function normaliseLayoutId(layoutId) {
  if (!layoutId) return DEFAULT_LAYOUT_ID;
  return LEGACY_LAYOUT_ALIASES[layoutId] || layoutId;
}

export function getLayout(layoutId) {
  const id = normaliseLayoutId(layoutId);
  return LAYOUTS.find((l) => l.id === id) || LAYOUTS.find((l) => l.id === DEFAULT_LAYOUT_ID);
}

/**
 * Versions — what the super-admin picks. Each version pins a single layout
 * and the list of colour themes the user is allowed to choose between in
 * their User Settings → Theme tab.
 */
export const VERSIONS = [
  {
    id: 'v1',
    name: 'Version 1 — Beta UI',
    description: 'Original SeaSuite shell. Users pick between Midnight, Sandstone or Ocean.',
    layoutId: 'beta',
    themeIds: ['midnight', 'sandstone', 'ocean'],
  },
  {
    id: 'v2',
    name: 'Version 2 — Aurora',
    description: 'New Aurora shell with top bar. Users pick between Aurora Light or Aurora Dark.',
    layoutId: 'aurora',
    themeIds: ['aurora-light', 'aurora-dark'],
  },
];

export const DEFAULT_VERSION_ID = 'v1';

export function getVersion(versionId) {
  return (
    VERSIONS.find((v) => v.id === versionId) || VERSIONS.find((v) => v.id === DEFAULT_VERSION_ID)
  );
}

export function getVersionForLayout(layoutId) {
  const normalised = normaliseLayoutId(layoutId);
  return VERSIONS.find((v) => v.layoutId === normalised) || getVersion(DEFAULT_VERSION_ID);
}

export function getVersionThemeIds(layoutId) {
  return getVersionForLayout(layoutId).themeIds;
}
